import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, TrendingUp, ChevronRight } from "lucide-react";

const BANDS = {
  high: { color: "bg-emerald-50 text-emerald-800 border-emerald-200", ring: "ring-emerald-200" },
  medium: { color: "bg-amber-50 text-amber-800 border-amber-200", ring: "ring-amber-200" },
  low: { color: "bg-slate-50 text-slate-700 border-slate-200", ring: "ring-slate-200" },
};

export default function RefundEstimator() {
  const [playbooks, setPlaybooks] = useState([]);
  const [merchantSlug, setMerchantSlug] = useState("");
  const [merchantName, setMerchantName] = useState("");
  const [daysCancel, setDaysCancel] = useState("2");
  const [daysCharge, setDaysCharge] = useState("5");
  const [hasConfirm, setHasConfirm] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/playbooks").then((r) => setPlaybooks(r.data)).catch(()=>{}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/estimate", {
        merchant_slug: merchantSlug || undefined,
        merchant_name: merchantName || undefined,
        days_since_cancel: parseInt(daysCancel) || 0,
        days_since_charge: parseInt(daysCharge) || 0,
        has_cancel_confirmation: hasConfirm,
      });
      setResult(r.data);
    } finally { setBusy(false); }
  };

  const band = result ? BANDS[result.likelihood] : null;

  return (
    <Card className="trust-card" data-testid="refund-estimator-card">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-5 h-5 text-[#1E3A8A]" />
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">Could this work for me?</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">30-second check — get an honest read on your refund odds.</p>

        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Merchant</Label>
            <select
              data-testid="estimator-merchant-select"
              value={merchantSlug}
              onChange={(e) => setMerchantSlug(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
            >
              <option value="">Not in the list…</option>
              {playbooks.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
            </select>
          </div>
          {!merchantSlug && (
            <div>
              <Label>Or type merchant</Label>
              <Input data-testid="estimator-merchant-name" value={merchantName} onChange={(e) => setMerchantName(e.target.value)} placeholder="e.g., LinkedIn Premium" />
            </div>
          )}
          <div>
            <Label>Days since you cancelled</Label>
            <Input data-testid="estimator-days-cancel" type="number" min="0" value={daysCancel} onChange={(e)=>setDaysCancel(e.target.value)} />
          </div>
          <div>
            <Label>Days since the charge</Label>
            <Input data-testid="estimator-days-charge" type="number" min="0" value={daysCharge} onChange={(e)=>setDaysCharge(e.target.value)} />
          </div>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700" data-testid="estimator-confirm-toggle">
            <Checkbox checked={hasConfirm} onCheckedChange={(v) => setHasConfirm(!!v)} />
            I have a cancellation confirmation (email or screenshot)
          </label>
          <div className="sm:col-span-2">
            <Button data-testid="estimator-submit" type="submit" disabled={busy} className="w-full sm:w-auto rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11 px-6">
              <Sparkles className="w-4 h-4 mr-2" /> {busy ? "Calculating…" : "Check my odds"}
            </Button>
          </div>
        </form>

        {result && (
          <div className={`mt-6 rounded-2xl border p-5 ring-4 ring-offset-0 ${band.color} ${band.ring}`} data-testid="estimator-result">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm uppercase tracking-widest font-semibold opacity-70">{result.merchant_name}</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1" data-testid="estimator-result-label">{result.label}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold" data-testid="estimator-result-score">{result.score}<span className="text-lg opacity-60">/100</span></p>
                <p className="text-xs opacity-70">Typical response: {result.estimated_response_days} days</p>
              </div>
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              {result.reasons.map((r, i) => (
                <li key={i} className="flex gap-2"><ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-60" /> {r}</li>
              ))}
            </ul>
            <Link to="/auth" data-testid="estimator-cta">
              <Button className="mt-5 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11 px-5">
                Start my case for $19
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
